/**
 * Storage rule (do not break):
 * - Form values, redux-form `time`, and API payloads use `HH:mm` on a 24-hour
 *   clock only. The booking instant (`time_stamp` / `time` ISO) is unchanged.
 * - `time_format` ("12" | "24") is display metadata only — not a second clock.
 * - 12h mode affects picker labels and `formatTimeForDisplay` output only.
 */

export type HourMode = "24" | "12";
export type Period = "AM" | "PM";

export const DEFAULT_HOUR = "14";
export const DEFAULT_MINUTE = "30";

const clampHour = (hour24: string) => {
  const parsed = Number.parseInt(hour24, 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(23, parsed));
};

export const to12h = (hour24: string): { hour: string; period: Period } => {
  const hour = clampHour(hour24);
  const face = hour % 12 === 0 ? 12 : hour % 12;
  return {
    hour: String(face).padStart(2, "0"),
    period: hour >= 12 ? "PM" : "AM",
  };
};

export const to24h = (hour12: string, period: Period): string => {
  const face =
    Math.max(1, Math.min(12, Number.parseInt(hour12, 10) || 12)) % 12;
  return String(period === "PM" ? face + 12 : face).padStart(2, "0");
};

export const splitTime = (
  value?: string
): { hour?: string; minute?: string } => {
  if (typeof value !== "string" || !value.includes(":")) return {};
  const [hour, minute] = value.split(":");
  return { hour: hour || undefined, minute: minute?.replace(/\s.*$/, "") || undefined };
};

const TIME_FORMAT_STORAGE_KEY = "BookingTimeFormat";

export const readStoredTimeFormat = (): HourMode | undefined => {
  try {
    const stored = window.localStorage.getItem(TIME_FORMAT_STORAGE_KEY);
    if (stored === "12" || stored === "24") return stored;
    const legacy = window.localStorage.getItem("yelo_time_format_v2");
    return legacy === "12" || legacy === "24" ? legacy : undefined;
  } catch {
    return undefined;
  }
};

export const storeTimeFormat = (mode: HourMode) => {
  try {
    window.localStorage.setItem(TIME_FORMAT_STORAGE_KEY, mode);
    window.localStorage.setItem("yelo_time_format_v2", mode);
  } catch {
    /* preference only */
  }
};

/** Normalize form/API time to `HH:mm`, including legacy `HH:mm AM` values. */
/** Normalize any user-facing string to `HH:mm` before save/submit. */
export const canonicalTime = (value?: string): string => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return trimmed;
  let hour = Number.parseInt(match[1], 10);
  const minute = match[2];
  const meridiem = match[3]?.toUpperCase() as Period | undefined;
  if (meridiem && hour <= 12) {
    hour = Number.parseInt(to24h(String(hour).padStart(2, "0"), meridiem), 10);
  }
  return `${String(clampHour(String(hour))).padStart(2, "0")}:${minute}`;
};

export const formatTimeForDisplay = (
  value: string | undefined,
  mode: HourMode,
  labels: { am: string; pm: string } = { am: "AM", pm: "PM" }
): string => {
  const normalized = canonicalTime(value);
  if (!normalized) return "";
  const { hour, minute } = splitTime(normalized);
  if (!hour || !minute || mode === "24") return normalized;
  const { hour: face, period } = to12h(hour);
  return `${Number.parseInt(face, 10)}:${minute} ${
    period === "AM" ? labels.am : labels.pm
  }`;
};

export const bookingTimeFormatOf = (
  booking?: { time_format?: unknown } | null
): HourMode => (booking?.time_format === "12" ? "12" : "24");
